import { CARD_COUNT, seededPicker, type TarotField, type TarotTextTable } from './tarot'
import type { Direction } from './tarotTopic'
import { escapeRe } from './escapeRe'

/**
 * **빠르게 물어보기** — 카드 한두 장으로 짧게 답하는 두 상품.
 *
 *   YES or NO      카드 1장 · 무료(로그인한 사람 하루 한 번) · 예/아니오와 짧은 이유
 *   둘 중 뭐가 나을까  카드 2장 · 3P · 선택지마다 한 장씩 놓고 견준다
 *
 * 뽑는 방식·카드 그림·뜻은 10장 타로와 **같은 것**을 쓴다 (`src/lib/tarot.ts` 한 벌).
 * 다른 것은 장수와 자리의 뜻뿐이다.
 *
 * 왜 3장(짧게 물어보기)과 따로 두나
 *   짧게 물어보기는 **정해진 질문 번호**를 받고 자리가 셋이다. 이쪽은 사용자가 쓴
 *   자유 문장을 받고 자리가 한둘이다. 저장 모양도 다르다.
 */

/** 글자 수 상한. 10장 타로의 자유 질문(120자)과 같은 값으로 둔다. */
export const QUICK_QUESTION_MAX = 120
/** 선택지 하나의 글자 수 상한. 짧게 적는 자리다. */
export const QUICK_OPTION_MAX = 40

export const YESNO_SIZE = 1
export const COMPARE_SIZE = 2

/** 예/아니오 두 값만 쓴다. "반반" 을 두면 물어본 사람이 아무것도 못 얻는다. */
export const YESNO_ANSWERS = ['yes', 'no'] as const
export type YesNoAnswer = (typeof YESNO_ANSWERS)[number]

export function isYesNoAnswer(v: unknown): v is YesNoAnswer {
  return typeof v === 'string' && (YESNO_ANSWERS as readonly string[]).includes(v)
}

export interface QuickCard {
  id: number
  direction: Direction
}

/**
 * 카드를 뽑는다. **같은 카드가 두 번 나오지 않는다.**
 * 씨앗이 같으면 같은 패다 (기록에서 다시 열 때·서버가 확인할 때).
 */
export function drawQuick(seed: string, size: number): QuickCard[] {
  const pick = seededPicker(seed)
  const used = new Set<number>()
  const out: QuickCard[] = []
  for (let i = 0; i < size; i += 1) {
    let id = pick(CARD_COUNT)
    for (let guard = 0; used.has(id) && guard < CARD_COUNT * 4; guard += 1) id = pick(CARD_COUNT)
    used.add(id)
    out.push({ id, direction: pick(2) === 0 ? 'upright' : 'reversed' })
  }
  return out
}

export const drawYesNo = (seed: string): QuickCard[] => drawQuick(seed, YESNO_SIZE)
export const drawCompare = (seed: string): QuickCard[] => drawQuick(seed, COMPARE_SIZE)

export interface QuickMaterial {
  cardId: number
  cardName: string
  direction: Direction
  /** 카드의 한 줄 뜻 */
  summary: string
  /** 그 자리에 맞춘 본문 */
  body: string
}

/** 카드 자료를 읽어 AI 에 넘길 재료를 만든다. **AI 없이** 자료만 쓴다. */
export function readQuick(cards: readonly QuickCard[], table: TarotTextTable, field: TarotField = 'advice'): QuickMaterial[] {
  return cards.map((card) => {
    const entry = table[String(card.id)]
    const side = card.direction === 'upright' ? entry?.upright : entry?.reversed
    return {
      cardId: card.id,
      cardName: entry?.name ?? String(card.id),
      direction: card.direction,
      summary: side?.summary ?? '',
      body: side?.[field] ?? side?.advice ?? '',
    }
  })
}

/** 씨앗이 정말 이 패를 뽑는가 (서버가 본다 — J1 과 같은 규칙) */
export function seedDrawsQuick(seed: string, size: number, cards: readonly { id: number; direction: Direction }[]): boolean {
  const drawn = drawQuick(seed, size)
  if (drawn.length !== cards.length) return false
  return drawn.every((d, i) => d.id === cards[i]!.id && d.direction === cards[i]!.direction)
}

const OPTION_QUOTES: Record<string, [string, string]> = { ko: ['「', '」'], ja: ['「', '」'] }

/**
 * **결론 글 속 선택지 이름은 따옴표로** — en "With these cards, Start a side project looks …" 처럼 사용자가 쓴
 * 선택지가 문장에 그대로 녹아 읽기 어려웠고, ja 「行くのほう」 처럼 동사 선택지 뒤 조사가 깨졌다 (「行く」のほう 는 맞다).
 * 이미 따옴표 안에 있으면 그대로 둔다. 대소문자는 보지 않는다.
 */
export function quoteOptions(text: string, options: readonly string[], locale: string): string {
  const [open, close] = OPTION_QUOTES[locale] ?? ['“', '”']
  let out = text
  for (const raw of [...options].sort((a, b) => b.length - a.length)) {
    const opt = raw.trim()
    if (!opt) continue
    const esc = escapeRe(opt)
    // 한·중·일은 낱말 사이 빈칸이 없거나 조사가 붙는다 — 앞이 따옴표가 아니면 된다. 라틴 글자는 낱말째만
    const spaced = !(locale === 'ko' || locale === 'ja' || locale.startsWith('zh'))
    const re = spaced
      ? new RegExp(`(^|[^「『“"'‘\\p{L}\\p{N}])(${esc})(?=$|[^」』”"'’\\p{L}\\p{N}])`, 'giu')
      : new RegExp(`(^|[^「『“"'‘])(${esc})(?![」』”"'’])`, 'gu')
    out = out.replace(re, (_m, before: string, found: string) => `${before}${open}${found}${close}`)
  }
  return out
}
